import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  styled,
  Container,
  Modal,
  CircularProgress,
  Paper,
  Stack
} from '@mui/material';
import { toast } from 'react-toastify';

import LandingPageNav from '../../components/landingpage/LandingPageNav';
import Footer from '../../components/Footer/footer';

import { apigetPublic, apidelete } from '../../service/api';

// --- Modal for Editing ---
import BranchEditModal from './Edit';

// -----------------------------
// Styled Components
// -----------------------------
const ProfileContainer = styled(Container)(() => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: 8,
  paddingTop: '84px',
  position: 'relative',
  zIndex: 1,
}));

const TopContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  marginBottom: theme.spacing(4),
  flexWrap: 'wrap',
}));

const BranchInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 300,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(2),
  fontSize: '1rem',
  textTransform: 'none',
  backgroundColor: '#b79a59 !important',
  color: '#fff !important',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: '#a5894f !important',
  },
}));

// -----------------------------

export default function BranchProfile() {
  const { id } = useParams(); // expecting `/dashboard/branches/:id`
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const [branchData, setBranchData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [openEdit, setOpenEdit] = useState(false);

  // -----------------------------------
  // Fetch Branch
  // -----------------------------------
  const fetchBranch = async () => {
    try {
      const res = await apigetPublic(`branches/${id}`);
      if (res?.status === 200) {
        setBranchData(res.data);
      }
    } catch (err) {
      toast.error('Failed to load branch');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranch();
  }, [id]);

  // -----------------------------------
  // Delete Branch
  // -----------------------------------
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this branch?')) return;

    try {
      const res = await apidelete(`branches/${branchData._id}`);
      if (res.status === 200) {
        toast.success('Branch deleted');
        navigate('/dashboard/branches');
      }
    } catch (err) {
      toast.error('Error deleting branch');
    }
  };

  // -----------------------------------
  // Loading State
  // -----------------------------------
  if (loading) {
    return (
      <Modal open>
        <Box sx={{
          position: 'absolute',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 300,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          textAlign: 'center'
        }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading Branch...</Typography>
        </Box>
      </Modal>
    );
  }

  if (!branchData) {
    return <Typography sx={{ mt: 10, textAlign: 'center' }}>Branch not found.</Typography>;
  }

  const canEdit = user?.role === 'admin' || user?.role === 'marketing';

  return (
    <>
      <LandingPageNav />

      <Box
        sx={{
          backgroundImage: 'url(https://res.cloudinary.com/ddhrdxc7d/image/upload/v1763995325/Background_Image_-_Blog_Pages_and_individual_LO_pages_1_w6e9ef.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(245,245,245,.85)',
          },
        }}
      >
        {/* Admin Buttons */}
        {canEdit && (
          <Box sx={{ p: 2 }}>
            <Button variant="outlined" onClick={() => setOpenEdit(true)}>
              Edit Branch
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={handleDelete}
              sx={{ ml: 2 }}
            >
              Delete
            </Button>
          </Box>
        )}

        <ProfileContainer>

          {/* Top Section */}
          <TopContainer>
            {/* Branch Image */}
            <Box>
              <img
                src={branchData.branchImageUrl}
                alt={branchData.name}
                style={{
                  width: 260,
                  height: 200,
                  objectFit: 'cover',
                  borderRadius: 12,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }}
              />
            </Box>

            {/* Info */}
            <BranchInfo>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {branchData.name}
              </Typography>

              <Typography variant="h5" sx={{ color: 'text.secondary', mt: 1 }}>
                {branchData.city}, {branchData.stateCode}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                {branchData.address}
              </Typography>

              {/* State Link */}
              {branchData.stateName && (
                <Typography
                  sx={{
                    mt: 2,
                    color: '#1976d2',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                  onClick={() => navigate(`/dashboard/branches/${branchData.stateCode}`)}
                >
                  View {branchData.stateName} State Profile
                </Typography>
              )}
            </BranchInfo>
          </TopContainer>

          {/* Loan Officers */}
          <Box sx={{ mt: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Loan Officers at {branchData.name}
            </Typography>

            {branchData.loanOfficers && branchData.loanOfficers.length > 0 ? (
              branchData.loanOfficers.map((lo) => (
                <Paper
                  key={lo._id}
                  sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center' }}
                >
                  <img
                    src={lo.imageUrl}
                    alt={lo.firstName}
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      marginRight: 20,
                    }}
                  />

                  <Box>
                    <Typography variant="h6">
                      {lo.firstName} {lo.lastName}
                    </Typography>
                    <Typography>{lo.title}</Typography>

                    <Button
                      variant="text"
                      onClick={() => navigate(`/${lo.slug}`)}
                      sx={{ mt: 1 }}
                    >
                      View Profile
                    </Button>
                  </Box>
                </Paper>
              ))
            ) : (
              <Typography>No loan officers found for this branch.</Typography>
            )}
          </Box>

        </ProfileContainer>
      </Box>

      {/* Edit Modal */}
      <BranchEditModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        branchData={branchData}
        onUpdate={fetchBranch}
      />

      <Footer />
    </>
  );
}
